<template>
  <div>
    <v-dialog scrollable v-model="newTicketDialog" max-width="1000px">
      <v-card height="auto">
        <v-card-title>
          <span class="text-h5">{{ $t("Create ticket") }}</span>
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" class="px-0">
              <v-text-field
                outlined
                :label="$t('Title')"
                required
                dense
                v-model="newTicket.Title"
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="12" class="px-0">
              <dragAndDropImage
                usage="newTicket"
                @deleteItemOutOfFile="deleteItemOutOfFile"
                @updateFiles="updateFiles"
              />
            </v-col>
            <v-col cols="6" class="pr-2">
              <v-row>
                <v-col class="pl-0" cols="12">
                  <v-combobox
                    hide-details="auto"
                    v-model="newTicket.PriorityId"
                    :items="priorties"
                    :label="$t('Priority')"
                    item-text="Name"
                    item-value="Id"
                    clearable
                    outlined
                    dense
                  >
                  </v-combobox>
                </v-col>
                <v-col class="pl-0" cols="12">
                  <v-combobox
                    hide-details="auto"
                    v-model="newTicket.StatusId"
                    :items="statusses"
                    :label="$t('Status')"
                    item-text="Name"
                    item-value="Id"
                    clearable
                    outlined
                    dense
                  >
                  </v-combobox>
                </v-col>
                <v-col class="pl-0" cols="12">
                  <v-menu
                    v-model="datePickerNewTicketStartDate"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="readableStartDate"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        hide-details="auto"
                        :label="$t('Start date')"
                        outlined
                        dense
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="getDateStart"
                      :locale="getBCPLangCode"
                      @input="datePickerNewTicketStartDate = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col class="pl-0" cols="12">
                  <v-menu
                    v-model="datePickerNewTicketReadyDate"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="readableReadyDate"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        hide-details="auto"
                        outlined
                        dense
                        :label="$t('Ready date')"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="getDateReady"
                      :locale="getBCPLangCode"
                      @input="datePickerNewTicketReadyDate = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col class="pl-0" cols="12">
                  <autocompleteDialogs
                    :Option="{
                      name: 'Reporter',
                      itemText: 'FullName',
                      itemValue: 'Id',
                      itemUrl: '/api/com-one/reporters',
                    }"
                    :selectedItem="newTicket.ReporterId"
                    @changeItem="$set(newTicket, 'ReporterId', $event)"
                  />
                </v-col>
              </v-row>
              <v-row class="mt-0">
                <v-col class="pl-0" cols="12">
                  <autocompleteDialogs
                    :Option="{
                      name: 'Users',
                      itemText: 'FullName',
                      itemValue: 'Id',
                      itemUrl: '/api/com-one/users',
                    }"
                    :selectedItem="newTicket.UserId"
                    @changeItem="$set(newTicket, 'UserId', $event)"
                  />
                </v-col>
                <v-col class="pl-0" cols="12">
                  <autocompleteDialogs
                    :Option="{
                      name: 'Assigning',
                      itemText: 'FullName',
                      itemValue: 'Id',
                      itemUrl: '/api/com-one/assignees',
                    }"
                    :selectedItem="newTicket.AssigneeId"
                    @changeItem="$set(newTicket, 'AssigneeId', $event)"
                  />
                </v-col>
                <v-col class="pl-0" cols="12">
                  <autocompleteDialogs
                    :Option="{
                      name: 'Author',
                      itemText: 'FullName',
                      itemValue: 'Id',
                      itemUrl: '/api/com-one/authors',
                    }"
                    :selectedItem="getAuthorModel"
                    @changeItem="$set(newTicket, 'AuthorId', $event)"
                  />
                </v-col>
                <v-col class="pl-0" cols="12">
                  <v-combobox
                    hide-details="auto"
                    v-model="newTicket.TrackerIds"
                    :items="trackers"
                    multiple
                    chips
                    :label="$t('Tracker')"
                    item-text="Name"
                    item-value="Id"
                    clearable
                    outlined
                    dense
                  >
                  </v-combobox>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="6" class="pl-2">
              <p class="mb-1 mt-1">{{ $t("Description") }}</p>
              <editor
                api-key="f18i0ywzo341k9tx3r3vjsnoghnomdrqbhx2qqi5lnul1dbh"
                v-model="getDiscription"
                :id="randomKey"
                :key="randomKey"
                :init="{
                  height: 200,
                  menubar: false,
                  language: editorLanguage,
                  plugins: [
                    'searchreplace visualblocks code fullscreen',
                    'insertdatetime media table paste code help wordcount',
                  ],
                  toolbar:
                    'undo redo | formatselect | bold italic underline strikethrough | link image | \
                                     alignleft aligncenter alignright alignjustify | \
                                     bullist numlist outdent indent | removeformat',
                }"
              />

              <v-layout>
                <v-spacer></v-spacer>
                <v-switch
                  v-model="switch1"
                  :disabled="getDisableCheck"
                  @change="setDialog($event)"
                  :label="$t('Send externally')"
                ></v-switch>
              </v-layout>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pr-6 pb-4 pt-0">
          <v-layout justify-end class="mt-4">
            <v-btn
              depressed
              @click="cancel(['newTicketDialog', 'showNewTicketDiscription'])"
            >
              {{ $t("Cancel") }}
            </v-btn>
            <v-btn
              depressed
              color="primary"
              :disabled="creatTicketCheck"
              :loading="newTicketIsLoading"
              @click="addNewTicket()"
              class="text-right ml-4"
            >
              {{ $t("Create") }}
            </v-btn>
          </v-layout>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="sendCommentExternallyDialog" scrollable width="900px">
      <v-card>
        <v-card-title>{{ $t("Send comment to customer?") }}</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col>
              <v-list class="py-0" v-if="!isLoadingUsersOfClient">
                <v-subheader class="pl-0" v-text="$t('Users')"></v-subheader>
                <v-list-item
                  v-for="(listItem, index) in listOfCustomersOfClient"
                  :key="index"
                  class="px-1"
                >
                  <v-checkbox
                    v-model="selectedUsers[index]"
                    color="primary"
                    :label="listItem.FullName"
                    :value="listItem.Id"
                    hide-details="auto"
                  ></v-checkbox>
                </v-list-item>
              </v-list>
              <preloader v-else />
            </v-col>
            <v-col>
              <v-list class="py-0">
                <v-subheader
                  class="pl-0"
                  v-text="$t('Internal users')"
                ></v-subheader>
                <v-list-item
                  v-for="(listItem, index) in listOfUsersOfClient"
                  :key="index"
                  :value="listItem"
                  class="px-1"
                >
                  <v-checkbox
                    v-model="selectedAssignee[index]"
                    color="primary"
                    :label="listItem.FullName"
                    :value="listItem.Id"
                    hide-details="auto"
                  ></v-checkbox>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col>
              <v-subheader
                class="pl-0"
                v-text="$t('Email addresses')"
              ></v-subheader>
              <v-form v-model="isFormValid">
                <v-text-field
                  dense
                  hide-details="auto"
                  v-model="email"
                  :rules="[
                    (v) =>
                      /^$|[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(v) ||
                      $i18n.translate('E-mail must be valid'),
                  ]"
                  class="px-1"
                >
                  <template v-slot:append-outer>
                    <v-icon
                      :disabled="!isFormValid"
                      @click="emails.push(email)"
                      color="success"
                    >
                      mdi-plus
                    </v-icon>
                  </template>
                </v-text-field>
              </v-form>
              <v-list v-if="emails.length !== 0">
                <v-list-item-group v-model="selectedEmails" multiple>
                  <v-list-item
                    v-for="(email, index) in emails"
                    :key="index"
                    :value="email"
                    dense
                    class="px-1"
                  >
                    <v-list-item-content v-text="email"> </v-list-item-content>
                    <v-list-item-action>
                      <v-icon @click="$delete(emails, index)">
                        mdi-delete
                      </v-icon>
                    </v-list-item-action>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pr-6 pb-4 pt-0">
          <v-layout justify-end class="mt-4">
            <v-btn @click="cancel(['sendCommentExternallyDialog'])">
              {{ $t("Cancel") }}
            </v-btn>
            <v-btn
              color="primary"
              @click="sendCommentExternallyDialog = false"
              class="ml-4"
            >
              {{ $t("Save") }}
            </v-btn>
          </v-layout>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
  
  <script>
import helpers from "@/helpers";
import request from "@/request";
import Editor from "@tinymce/tinymce-vue";
import autocompleteDialogs from "../dialogs/AutocompleteDialogs.vue";
import dragAndDropImage from "../dialogs/DragAndDrop.vue";

export default {
  props: [
    "newTicket",
    "pagingForFilter",
    "priorties",
    "statusses",
    "authors",
    "trackers",
    "user",
    "users",
  ],
  data() {
    return {
      newTicketDialog: null,
      datePickerNewTicketStartDate: null,
      datePickerNewTicketReadyDate: null,
      newTicketIsLoading: false,
      isLoading: false,
      sendCommentExternallyDialog: false,
      listOfCustomersOfClient: null,
      listOfUsersOfClient: null,
      selectedAssignee: [],
      selectedUsers: [],
      selectedEmails: [],
      isFormValid: true,
      emails: [],
      email: null,
      isLoadingUsersOfClient: false,
      isLoadingCustomersOfClient: false,
      switch1: false,
      randomKey: Math.floor(Math.random() * 100) + "newTicketDesciriptuion",
    };
  },
  components: { editor: Editor, autocompleteDialogs, dragAndDropImage },
  computed: {
    editorLanguage: {
      get: function () {
        if (!this.$i18n.locale()) {
          return "nl";
        }
        return this.$i18n.locale() == "Nl" ? "nl" : "fr_FR";
      },
    },

    readableStartDate: {
      get() {
        return helpers.changeTimeToReadeble(this.newTicket.StartDate, false);
      },
    },
    readableReadyDate: {
      get() {
        return helpers.changeTimeToReadeble(this.newTicket.ReadyDate, false);
      },
    },
    getAllUsers() {
      let users = [];
      if (this.users) {
        users = this.users;
        users.forEach((element) => {
          element.FullName =
            element.FullName +
            " (" +
            element.ClientName.substring(0, 20) +
            "...)";
        });
      }
      return users;
    },
    creatTicketCheck() {
      if (!this.newTicket) {
        return true;
      }
      if (this.newTicket.PriorityId && this.newTicket.StatusId) {
        return false;
      }
      return true;
    },
    getAuthorModel: {
      get() {
        return this.newTicket.AuthorId;
      },
      set(newItem) {
        this.newTicket.AuthorId = newItem;
      },
    },
    getDateReady: {
      get() {
        let date = new Date(this.newTicket.ReadyDate);
        return helpers.changeTimeToReadeble(date);
      },
      set(newVal) {
        this.newTicket.ReadyDate = newVal;
      },
    },
    getDateStart: {
      get() {
        let date = new Date(this.newTicket.StartDate);
        return helpers.changeTimeToReadeble(date);
      },
      set(newVal) {
        this.newTicket.StartDate = newVal;
      },
    },
    getBCPLangCode() {
      return helpers.getBCPLangCode(this.currentLanguage);
    },
    getAuthUserId() {
      if (this.user["_id"]) {
        return this.user["_id"];
      }
      if (this.user["Id"]) {
        return this.user["Id"];
      }
      return "";
    },
    getDisableCheck() {
      if (
        this.newTicket.ReporterId &&
        this.newTicket.UserId &&
        this.newTicket.AuthorId &&
        this.newTicket.AssigneeId
      ) {
        return false;
      }
      return true;
    },
    getDiscription: {
      get() {
        return this.newTicket.Description;
      },
      set(newItem) {
        this.newTicket.Description = newItem;
      },
    },
  },
  methods: {
    setDialog(event) {
      this.isLoadingUsersOfClient = true;
      this.isLoadingCustomersOfClient = true;
      request.get(
        "/api/com-one/get-users-to-notify-by-properties?reporterId=" +
          this.newTicket.ReporterId.Id +
          "&assigneeId=" +
          this.newTicket.AssigneeId.Id +
          "&authorId=" +
          this.newTicket.AuthorId.Id +
          "&userId=" +
          this.newTicket.UserId.Id,
        null,
        (res) => {
          if (res.success) {
            const requestsCustomers = [];
            const requestsUsers = [];
            if (res.CustomerUserIdsToNotify.length) {
              res.CustomerUserIdsToNotify.forEach((element) => {
                requestsCustomers.push("/api/com-one/user?id=" + element);
              });
            }
            if (res.UserIdsToNotify.length) {
              res.UserIdsToNotify.forEach((element) => {
                requestsUsers.push("/api/com-one/author?id=" + element);
              });
            }
            request.getAll(requestsCustomers, null, (result) => {
              this.listOfCustomersOfClient = result;
              this.isLoadingCustomersOfClient = true;
            });
            request.getAll(requestsUsers, null, (result) => {
              this.listOfUsersOfClient = result;
              this.isLoadingUsersOfClient = false;
            });
          }
        }
      );
      this.sendCommentExternallyDialog = event;
    },
    deleteItemOutOfFile(event) {
      let arr = Array.from(this[event.use].Files.length);
      if (this[event.use].Files.length >= 1) {
        for (let i = 0; i < this[event.use].Files.length; i++) {
          if (this[event.use].Files[i].name == event.value) {
            arr.splice(i, 1);
          }
        }
        this[event.use].Files = arr;
      }
    },
    setAuthorForNewTicket() {
      if (this.authors) {
        this.newTicket.AuthorId = this.getAuthor(this.getAuthUserId);
      }
    },
    cancel(items) {
      items.forEach((element) => {
        this[element] = false;
      });
    },
    updateFiles(event) {
      this[event.use].Files = event.value;
    },
    addNewTicket() {
      this.loadingTickets = true;
      this.newTicketIsLoading = true;
      let trackerIds = [];
      if (this.newTicket.TrackerIds) {
        this.newTicket.TrackerIds.forEach((element) => {
          trackerIds.push(element.Id);
        });
      }
      let item = {
        Title: this.newTicket.Title ? this.newTicket.Title : "",
        Description: this.newTicket.Description,
        AuthorId: this.newTicket.AuthorId ? this.newTicket.AuthorId.Id : "",
        AssigneeId: this.newTicket.AssigneeId
          ? this.newTicket.AssigneeId.Id
          : "",
        ReporterId: this.newTicket.ReporterId
          ? this.newTicket.ReporterId.Id
          : "",
        UserId: this.newTicket.UserId ? this.newTicket.UserId.Id : "",
        PriorityId: this.newTicket.PriorityId
          ? this.newTicket.PriorityId.Id
          : "",
        StatusId: this.newTicket.StatusId ? this.newTicket.StatusId.Id : "",
        TrackerIds: trackerIds,
        StartDate: this.newTicket.StartDate,
        ReadyDate: this.newTicket.ReadyDate,
        CustomerUserIdsToNotify: this.selectedUsers,
        UserIdsToNotify: this.selectedAssignee,
        EmailAddressesToNotify: this.emails,
        Internal: !this.switch1,
      };
      request.post("/api/com-one/ticket", item, (res) => {
        if (res.success) {
          if (this["newTicket"]["Files"]) {
            let formData = new FormData();
            for (var i = 0; i < this["newTicket"].Files.length; i++) {
              formData.append("files", this["newTicket"].Files[i]);
            }
            request.post(
              "/api/com-one/upload-file?ticketId=" + res.Id,
              formData,
              () => {
                this.loadingTickets = false;
                this.newTicketIsLoading = false;
                this.newTicketDialog = false;
              }
            );
          }
          this.newTicket.Description = null;
          this.loadingTickets = false;
          this.newTicketIsLoading = false;
          this.newTicketDialog = false;
          this.switch1 = false;
          this.selectedUsers = [];
          this.selectedAssignee = [];
          this.emails = [];
          this.$emit("getFiltersFromChild", this.pagingForFilter);
          this.$emit("resetNewTicket");
          this.$emit("getOverview");
        }
      });
    },
    setTicketDialog(event) {
      this.randomKey =
        Math.floor(Math.random() * 100) + "newTicketDesciriptuion";
      this.newTicketDialog = event;
    },
  },
};
</script>
  
  <style>
</style>